import { inject, injectable } from 'inversify';
import { Logger } from '../models/logger';
import { MetricEventName } from '../types/metric-events';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { ApiConfig } from './api-config';
import { HttpClient } from './http-client';
import { SocketJSService } from './socketjs.service';
import { WebSocketService } from './websocket.service';

interface MetricEvent {
    event: MetricEventName;
    attributes: Record<string, string | number | boolean>;
    timestamp: string;
    pageUrl: string;
    wsId: string;
    sessionId: string;
    platform: string;
}

@injectable()
export class MetricsService {
    private readonly logger = Logger.getLogger('MetricsService');

    private buffer: MetricEvent[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly MAX_BUFFER_SIZE = 10;
    private readonly FLUSH_INTERVAL_MS = 5000;

    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(HttpClient) private http: HttpClient,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(SocketJSService) private socketJSService: SocketJSService,
    ) { }

    emit(event: MetricEventName, attributes: Record<string, string | number | boolean> = {}) {
        if (!BrowserUtils.hasWindow()) return;

        this.buffer.push({
            event,
            attributes,
            timestamp: new Date().toISOString(),
            pageUrl: window.location.pathname,
            wsId: this.wsService.getOrCreateStoredWSId(),
            sessionId: this.socketJSService.sessionId,
            platform: this.detectPlatform(),
        });

        if (this.buffer.length >= this.MAX_BUFFER_SIZE) {
            this.flush();
        } else if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => this.flush(), this.FLUSH_INTERVAL_MS);
        }
    }

    flush() {
        if (this.buffer.length === 0) return;
        const batch = this.buffer.splice(0);
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        try {
            this.http.post(this.apiConfig.getMetricsEndpoint(), { events: batch }).catch(this.logger.error);
        } catch { /* metrics are best-effort */ }
    }

    flushOnUnload() {
        if (this.buffer.length === 0) return;
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            const payload = JSON.stringify({ events: this.buffer.splice(0) });
            navigator.sendBeacon(this.apiConfig.getMetricsEndpoint(), payload);
        } else {
            this.flush();
        }
    }

    private detectPlatform(): string {
        if (typeof window === 'undefined') return 'server';
        const w = window.innerWidth;
        if (w < 768) return 'mobile';
        if (w < 1024) return 'tablet';
        return 'desktop';
    }
}
